import type { UseControllerProps } from 'react-hook-form'
import type { DataOption, GroupBase, OptionsOrGroups } from './types'

import React from 'react'
import { CircleHelpIcon } from 'lucide-react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/shared/components/ui/form'
import {
	MultiSelect,
	MultiSelectCheckboxItem,
	MultiSelectCheckboxSelectAll,
	MultiSelectContent,
	MultiSelectEmpty,
	MultiSelectGroup,
	MultiSelectItem,
	MultiSelectList,
	MultiSelectSearch,
	MultiSelectSeparator,
	MultiSelectTrigger,
	MultiSelectValue,
	useMultiSelect,
} from '@/shared/components/ui/multi-select'
import {
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from '@/shared/components/ui/tabs'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'
import {
	Virtualized,
	VirtualizedVirtualizer,
} from '@/shared/components/ui/virtualizer'

import { gatFlatOptions, isGrouped } from './utils'

export default function RHFMultiSelectSegmented<OptionType extends DataOption>({
	name,
	label,
	hint,
	shouldUnregister,
	defaultValue,
	description,
	options,
	disabled,
	placeholder,
	className,
	enableVirtualization = true,
	isLoading,
	isOptionDisabled,
}: UseControllerProps & {
	options: OptionsOrGroups<OptionType, GroupBase<OptionType>>
} & {
	placeholder?: string
	className?: string
	disabled?: boolean
	label: string
	hint?: string
	description?: string
	enableVirtualization?: boolean
	isLoading?: boolean
	isOptionDisabled?: (option: OptionType) => boolean
}) {
	const { t } = useTranslation()

	const { control } = useFormContext()
	const flatOptions = React.useMemo(() => gatFlatOptions(options), [options])

	return (
		<FormField
			name={name}
			defaultValue={defaultValue}
			shouldUnregister={shouldUnregister}
			control={control}
			render={({ field, fieldState: { error } }) => (
				<FormItem>
					<FormLabel>
						{label}
						{hint && (
							<Tooltip>
								<TooltipTrigger asChild>
									<CircleHelpIcon className='size-3 text-muted-foreground' />
								</TooltipTrigger>
								<TooltipContent side='top'>{hint}</TooltipContent>
							</Tooltip>
						)}
					</FormLabel>
					<MultiSelect
						isLoading={isLoading}
						disabled={disabled}
						options={flatOptions}
						onSearch={enableVirtualization ? () => {} : undefined}
						onValueChange={field.onChange}
						value={field.value}
					>
						<FormControl>
							<MultiSelectTrigger
								data-name={name}
								ref={field.ref}
								aria-invalid={!!error}
								className={className}
							>
								<MultiSelectValue
									maxDisplay={2}
									placeholder={placeholder ?? t('general_select')}
								/>
							</MultiSelectTrigger>
						</FormControl>
						{enableVirtualization ? (
							<MultiSelectContent>
								<MultiSelectVirtualContent
									options={flatOptions}
									isOptionDisabled={isOptionDisabled}
								/>
							</MultiSelectContent>
						) : (
							<MultiSelectContent>
								<Tabs defaultValue='all' className='overflow-hidden'>
									<TabsList className='w-full'>
										<TabsTrigger value='all' data-tab='all'>
											All
										</TabsTrigger>
										<TabsTrigger value='selected' data-tab='selected'>
											Selected
										</TabsTrigger>
									</TabsList>
									<MultiSelectSearch placeholder={t('general_search')} />
									<TabsContent value='all' asChild>
										<ItemsList
											options={options}
											isOptionDisabled={isOptionDisabled}
										/>
									</TabsContent>
									<TabsContent value='selected' asChild>
										<ItemsList
											options={options}
											isOptionDisabled={isOptionDisabled}
										/>
									</TabsContent>
								</Tabs>
							</MultiSelectContent>
						)}
					</MultiSelect>
					{!!description && <FormDescription>{description}</FormDescription>}
					<FormMessage />
				</FormItem>
			)}
		/>
	)
}

function MultiSelectVirtualContent<T extends DataOption>({
	options,
	isOptionDisabled,
}: {
	options: T[]
	isOptionDisabled?: (option: T) => boolean
}) {
	const { t } = useTranslation()

	const { value } = useMultiSelect()
	const [searchText, setSearchText] = React.useState('')
	const filteredOptions = options.filter((option) =>
		option.label.toLowerCase().includes(searchText.toLowerCase() ?? [])
	)

	return (
		<Tabs defaultValue='all' className='gap-0 overflow-hidden'>
			<MultiSelectSearch
				onValueChange={setSearchText}
				value={searchText}
				className='rounded-sm'
				placeholder={t('general_search')}
			/>
			<div className='border-b p-2'>
				<TabsList className='bg-transparent'>
					<TabsTrigger value='all' data-tab='all'>
						{t('general_all')}
					</TabsTrigger>
					<TabsTrigger value='selected' data-tab='selected'>
						{t('general_selected')} ({value.length || 0})
					</TabsTrigger>
				</TabsList>
			</div>
			<TabsContent className='border-t' value='all' asChild>
				<VirtualItemsList
					options={filteredOptions}
					isOptionDisabled={isOptionDisabled}
				/>
			</TabsContent>
			<TabsContent value='selected' asChild>
				<VirtualItemsList
					options={filteredOptions.filter((o) => value.includes(o.value))}
					isOptionDisabled={isOptionDisabled}
				/>
			</TabsContent>
		</Tabs>
	)
}

const ItemsList = <OptionType extends DataOption>({
	options,
	isOptionDisabled,
}: {
	options: OptionsOrGroups<OptionType, GroupBase<OptionType>>
	isOptionDisabled?: (option: OptionType) => boolean
}) => (
	<MultiSelectList className='max-h-[calc(var(--radix-popper-available-height)-72px)]'>
		{isGrouped(options)
			? (options as GroupBase<OptionType>[]).map((optionGroup) => (
					<MultiSelectGroup key={optionGroup.label} heading={optionGroup.label}>
						{optionGroup.options?.map((option) => (
							<MultiSelectItem
								key={option.value}
								value={option.value}
								keywords={[option.label]}
								disabled={isOptionDisabled?.(option)}
								data-value={option.value}
							>
								{option.label}
							</MultiSelectItem>
						))}
					</MultiSelectGroup>
				))
			: (options as OptionType[]).map((option) => (
					<MultiSelectItem
						value={option.value}
						key={option.value}
						keywords={[option.label]}
						data-value={option.value}
					>
						{option.label}
					</MultiSelectItem>
				))}
	</MultiSelectList>
)
const VirtualItemsList = <T extends DataOption>({
	options,
	isOptionDisabled,
}: {
	options: T[]
	isOptionDisabled?: (option: T) => boolean
}) => {
	const { t } = useTranslation()

	return (
		<Virtualized asChild>
			<MultiSelectList className='max-h-[calc(var(--radix-popper-available-height)-72px)] scroll-pt-12'>
				<MultiSelectEmpty />
				{!!options.length && (
					<MultiSelectGroup className='sticky top-0 z-10 bg-popover'>
						<MultiSelectCheckboxSelectAll
							values={options
								.filter((o) => (isOptionDisabled ? !isOptionDisabled(o) : true))
								.map(({ value }) => value)}
						>
							{t('general_select_all')}
						</MultiSelectCheckboxSelectAll>
					</MultiSelectGroup>
				)}
				<MultiSelectSeparator className='sticky top-10' />
				<MultiSelectGroup>
					<VirtualizedVirtualizer>
						{options.map((option) => (
							<MultiSelectCheckboxItem
								key={option.value}
								keywords={[option.label]}
								value={option.value}
								disabled={isOptionDisabled?.(option)}
								data-value={option.value}
							>
								{option.label}
							</MultiSelectCheckboxItem>
						))}
					</VirtualizedVirtualizer>
				</MultiSelectGroup>
			</MultiSelectList>
		</Virtualized>
	)
}
